import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type {
  OnboardingCaseType,
  ProfileDraft,
  WizardCaseInfo,
  WizardProfileData,
  WizardState,
  WizardStep,
} from "./types";

const STEP_ORDER: WizardStep[] = ["case-information", "profile", "review"];

type EntityClientWizardContextValue = WizardState & {
  goNext: () => void;
  goBack: () => void;
  goToStep: (step: WizardStep) => void;
  footerContent: ReactNode;
  setFooterContent: (content: ReactNode) => void;
  requestExit: () => void;
  setRequestExit: (handler: () => void) => void;
  setCaseInfo: (info: WizardCaseInfo | null) => void;
  setNewProfileData: (data: WizardProfileData | null) => void;
  setProfileDraft: (draft: ProfileDraft | null) => void;
  setProductLine: (productLine: string | undefined) => void;
  setProductLineLoading: (loading: boolean) => void;
};

const EntityClientWizardContext = createContext<EntityClientWizardContextValue | null>(null);

export const EntityClientWizardProvider = ({
  caseType,
  children,
}: {
  caseType: OnboardingCaseType;
  children: React.ReactNode;
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>("case-information");
  const [footerContent, setFooterContent] = useState<ReactNode>(null);
  const [requestExit, setRequestExitState] = useState<() => void>(() => () => undefined);
  const [caseInfo, setCaseInfo] = useState<WizardCaseInfo | null>(null);
  const [newProfileData, setNewProfileData] = useState<WizardProfileData | null>(null);
  const [profileDraft, setProfileDraft] = useState<ProfileDraft | null>(null);
  const [productLine, setProductLine] = useState<string | undefined>(undefined);
  const [isProductLineLoading, setProductLineLoading] = useState(false);

  const goNext = useCallback(() => {
    setCurrentStep((prev) => {
      const idx = STEP_ORDER.indexOf(prev);
      return STEP_ORDER[idx + 1] ?? prev;
    });
  }, []);

  const goBack = useCallback(() => {
    setCurrentStep((prev) => {
      const idx = STEP_ORDER.indexOf(prev);
      return STEP_ORDER[idx - 1] ?? prev;
    });
  }, []);

  const goToStep = useCallback((step: WizardStep) => {
    setCurrentStep(step);
  }, []);

  const setRequestExit = useCallback((handler: () => void) => {
    setRequestExitState(() => handler);
  }, []);

  const value = useMemo<EntityClientWizardContextValue>(
    () => ({
      currentStep,
      caseType,
      caseInfo,
      newProfileData,
      profileDraft,
      productLine,
      isProductLineLoading,
      goNext,
      goBack,
      goToStep,
      footerContent,
      setFooterContent,
      requestExit,
      setRequestExit,
      setCaseInfo,
      setNewProfileData,
      setProfileDraft,
      setProductLine,
      setProductLineLoading,
    }),
    [
      currentStep,
      caseType,
      caseInfo,
      newProfileData,
      profileDraft,
      productLine,
      isProductLineLoading,
      goNext,
      goBack,
      goToStep,
      footerContent,
      requestExit,
      setRequestExit,
    ],
  );

  return (
    <EntityClientWizardContext.Provider value={value}>
      {children}
    </EntityClientWizardContext.Provider>
  );
};

export const useEntityClientWizard = (): EntityClientWizardContextValue => {
  const ctx = useContext(EntityClientWizardContext);
  if (!ctx) {
    throw new Error("useEntityClientWizard must be used inside EntityClientWizardProvider");
  }
  return ctx;
};
